import {useMemo} from 'react';

import {ConfigType} from 'coreComponents/Carousel/ICarousel';
import useClientServerEffect from 'coreHooks/useClientServerEffect';
import useSSRStyle from 'coreHooks/useSSRStyle';

import {processStyles} from './helpers';
import {IProcessStylesConfig, ISsrStyleEntry} from './ICarouselSSRStyle';

/**
 * SSR rules only have to hold until Swiper initializes: from then on Swiper applies the
 * equivalent inline styles itself. Keeping them active afterwards would interfere with its
 * measurements (e.g. with `slidesPerView: 'auto'` the CSS margin is added to the slide size
 * on top of `spaceBetween`, doubling the spacing).
 */
const PRE_INIT_SCOPE = '.swiper:not(.swiper-initialized)';

/**
 * Generates dynamic CSS rules for a carousel based on its config and adds
 * them to the global SSR style pool.
 *
 * Each config combination produces a unique hashed className (e.g. `cr-123456`)
 * that is applied to the carousel wrapper. The corresponding CSS rules are
 * deduplicated in Redux by that hash — so carousels sharing the same
 * config reuse the same rule without duplication.
 *
 * On unmount, the generated rules are removed from the pool.
 *
 * @returns An object containing the generated className(s) to apply to the carousel.
 */
const useCarouselSSRStyle = (carouselConfig: ConfigType) => {
	const {addStyle, deleteStyle} = useSSRStyle();
	const styleConfigKey = JSON.stringify({
		slidesPerView: carouselConfig.slidesPerView,
		spaceBetween: carouselConfig.spaceBetween,
		slidesOffsetBefore: carouselConfig.slidesOffsetBefore,
		slidesOffsetAfter: carouselConfig.slidesOffsetAfter,
		breakpoints: carouselConfig.breakpoints,
		centeredSlides: carouselConfig.centeredSlides,
		coverflowEffectConfig: carouselConfig.coverflowEffectConfig,
	});

	const {className, entries} = useMemo(() => {
		const breakpoints = Object.fromEntries(
			Object.entries(carouselConfig.breakpoints || {}).map(([breakpoint, breakpointConfig]) => [
				breakpoint,
				{
					...breakpointConfig,
					slidesOffsetBefore: breakpointConfig.slidesOffsetBefore ?? carouselConfig.slidesOffsetBefore,
					slidesOffsetAfter: breakpointConfig.slidesOffsetAfter ?? carouselConfig.slidesOffsetAfter,
				},
			]),
		);

		// Order matters: `processStyles` merges transform rules cumulatively per class name.
		const styleConfigs: IProcessStylesConfig[] = [
			{
				configKey: {
					slidesPerView: carouselConfig.slidesPerView,
					spaceBetween: carouselConfig.spaceBetween,
					slidesOffsetBefore: carouselConfig.slidesOffsetBefore,
					slidesOffsetAfter: carouselConfig.slidesOffsetAfter,
					breakpoints: breakpoints
				},
				generateStyleFn: 'generateSlidesPerViewStyle',
				selector: `${PRE_INIT_SCOPE} > .{{currentClassName}} > .swiper-slide`,
			},
			{
				configKey: {
					spaceBetween: carouselConfig.spaceBetween,
					breakpoints: carouselConfig.breakpoints
				},
				generateStyleFn: 'generateSpaceBetweenStyle',
				selector: `${PRE_INIT_SCOPE} > .{{currentClassName}} > .swiper-slide`,
			},
			{
				configKey: {
					slidesOffsetBefore: carouselConfig.slidesOffsetBefore,
					breakpoints: carouselConfig.breakpoints
				},
				generateStyleFn: 'generateSlidesOffsetStyle',
				selector: `${PRE_INIT_SCOPE} > div.{{currentClassName}}`,
			},
			{
				configKey: {
					centeredSlides: carouselConfig.centeredSlides,
					slidesPerView: carouselConfig.slidesPerView,
					breakpoints: carouselConfig.breakpoints
				},
				generateStyleFn: 'generateCenteredSlidesStyle',
				selector: `${PRE_INIT_SCOPE} > div.{{currentClassName}}`,
			},
			{
				configKey: {
					coverflowEffectConfig: carouselConfig.coverflowEffectConfig,
					breakpoints: carouselConfig.breakpoints
				},
				generateStyleFn: 'generateCoverflowEffectSlideStyle',
				selector: `${PRE_INIT_SCOPE} > div.{{currentClassName}} > .swiper-slide:not(:nth-child(1))`,
			},
		];

		const styleEntries = styleConfigs
			.map((styleConfig) => processStyles(styleConfig))
			.filter(Boolean) as ISsrStyleEntry[];

		return {
			className: styleEntries.map(({className: cn}) => cn).join(' '),
			entries: styleEntries,
		};
	}, [styleConfigKey]);

	useClientServerEffect(() => {
		entries.forEach(({className: cn, style}) => addStyle(cn, style));

		return () => entries.forEach(({className: cn}) => deleteStyle(cn));
	}, [entries]);

	return {className};
};

export default useCarouselSSRStyle;
